import { useState } from "react";
import { Link } from "react-router-dom";

import { useMarketLots } from "../api/queries";
import "./pages.css";
import { Empty } from "../components/Empty";
import { ErrorBand } from "../components/ErrorBand";
import { LotItems } from "../components/LotItems";
import { Panel } from "../components/Panel";
import { Skeleton } from "../components/Skeleton";
import { TitleBand } from "../components/TitleBand";
import { adena, ago, plural, when } from "../format";
import { traderHref } from "../search/nav";
import type { MarketLotSort } from "./marketSort";
import { filterMarketLots, isStale, sortMarketLots } from "./marketSort";

const SORTS: MarketLotSort[] = ["seen", "trader", "total"];
const SORT_LABELS: Record<MarketLotSort, string> = {
  seen: "last seen", trader: "trader", total: "lot price",
};

/** Every distinct lot ever scanned. A lot parked in a store for a fortnight is
 *  one row with a span, not forty — the server folds the sightings, and `Seen`
 *  says how many there were. */
export function MarketPackages() {
  const [sort, setSort] = useState<MarketLotSort>("seen");
  const [filter, setFilter] = useState("");

  const market = useMarketLots();

  if (market.isError) {
    return (
      <>
        <TitleBand title="Package sales" />
        <ErrorBand
          message="Could not load the package sales."
          hint="The server may be restarting. Reload in a moment."
        />
      </>
    );
  }
  if (!market.data) {
    return <><TitleBand title="Package sales" /><Skeleton rows={6} /></>;
  }

  const { lots: all, modes, truncated } = market.data;
  const lots = sortMarketLots(filterMarketLots(all, filter), sort);
  const needle = filter.trim();

  return (
    <>
      <TitleBand
        title="Package sales"
        note={`${plural(lots.length, "lot")}, sold whole`}
      />
      <Panel primary>
        <div className="strip">
          <span>Sort</span>
          {SORTS.map((option) => (
            <button
              key={option}
              type="button"
              aria-pressed={sort === option}
              onClick={() => setSort(option)}
            >
              {SORT_LABELS[option]}
            </button>
          ))}
          <span style={{ flex: 1 }} />
          <input
            aria-label="Filter items"
            placeholder="filter item…"
            value={filter}
            onChange={(e) => setFilter(e.target.value)}
          />
        </div>

        {truncated ? (
          <p className="scan-meta">
            showing the most recently seen lots — the history holds more
          </p>
        ) : null}

        {/* Four states, told apart. A mode nobody ever walked is not a market
            with nothing in it, and neither is a deployment that has never
            scanned at all. */}
        {lots.length === 0 ? (
          needle ? (
            <Empty>Nothing matching “{needle}”.</Empty>
          ) : modes.length === 0 ? (
            // Nothing has ever been scanned at all. `modes` is the union of every
            // mode any scan ever walked, so an empty one means there are no scans
            // — not that scans ran without walking packages. This is the first
            // thing a fresh deployment shows, and it should say what to do next
            // rather than describe a scan setting nobody has had the chance to
            // choose yet.
            <Empty>
              No scans yet. Run <code>python -m market_analyzer scan</code> on the
              scanning machine, then <code>sync</code>.
            </Empty>
          ) : modes.includes("package") ? (
            <Empty>No package sales have ever been scanned.</Empty>
          ) : (
            <Empty>
              No scan has ever walked packages. Run a scan with package enabled
              to see them here.
            </Empty>
          )
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>Trader</th>
                  <th className="num">Lot price</th>
                  <th className="num">Items</th>
                  <th>Included</th>
                  <th className="num">Seen</th>
                  <th className="num">Last seen</th>
                </tr>
              </thead>
              <tbody>
                {lots.map((lot) => (
                  // Nick and price alone are not unique: two traders' lots can
                  // agree on both, and one trader can park two lots at the same
                  // total. The item ids are what tell those apart.
                  <tr key={`${lot.nick}-${lot.total ?? "unpriced"}-${lot.items.map((i) => i.id).join("|")}`}>
                    <td><Link to={traderHref(lot.nick)}>{lot.nick}</Link></td>
                    {/* No buy/sell hue: a lot is neither a bid nor an ask. */}
                    <td className="num">{adena(lot.total)}</td>
                    <td className="num dim">{lot.items.length}</td>
                    <td><LotItems items={lot.items} /></td>
                    <td
                      className="num dim"
                      title={`First seen ${when(lot.first_seen)}`}
                    >
                      {lot.sightings}
                    </td>
                    <td
                      className={`num ${isStale(lot.last_seen) ? "dim" : ""}`}
                      title={when(lot.last_seen)}
                    >
                      {ago(lot.last_seen)}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}
